// eslint-disable-next-line @typescript-eslint/ban-ts-comment -- pi-lens/Deno resolver false positives on Expo/React workspace imports in this RN component; repo-native toolchain resolves these correctly
// @ts-nocheck
/**
 * AnalysisSection - Message input and analysis controls
 *
 * Extracted from index.tsx to maintain <300 line limit
 * Contains: ContextSelector, MessageInput, TurnstileWidget, Analyze Button
 *
 * Mobile Web Keyboard Optimization:
 * - Shows compact inline dropdowns (Experience + Context) when keyboard is visible
 * - Maximizes textarea space during typing
 * - Matches HTML prototype: dramadetector_mobile_1_focused.html
 */
// @ts-expect-error -- pi-lens fallback Deno checker cannot resolve react-native workspace imports here
import { View, Platform, StyleSheet } from "react-native";
// @ts-expect-error -- pi-lens fallback Deno checker cannot resolve React workspace imports here
import React from "react";
// @ts-expect-error -- pi-lens fallback Deno checker cannot resolve local workspace package here
import { TierBody } from "@dramadetector/ui";
import { MessageInput } from "../../../components/MessageInput";
import { ContextSelector } from "../../../components/ContextSelector";
import { TurnstileWidget } from "../../../components/TurnstileWidget";
// @ts-expect-error -- pi-lens fallback Deno checker cannot resolve Expo workspace imports here
import { useRouter } from "expo-router";
import { CompactReadOnlyIndicators } from "./CompactReadOnlyIndicators";
import { AnonymousQuotaCounter } from "./AnonymousQuotaCounter";
import type { ContextType } from "../../../hooks/useMessageInput";
import type { TheatricalModeVariant } from "../../../lib/theatrical/theatricalModeConfig";
import type { TurnstileHandle } from "../../../lib/turnstile/turnstileConfig";
import { useMultimodalFlag } from "../../../hooks/useFeatureFlags";

interface AnalysisSectionProps {
	message: string;
	context: ContextType;
	validationError: string | null;
	isValid: boolean;
	/** Maximum character limit (varies by subscription tier: 500/1000/2000/5000) */
	maxLength: number;
	turnstileToken: string | null;
	turnstileError: string | null;
	/** DRAMA-WEB-28: Clearance token (hides Turnstile widget when present) */
	clearanceToken: string | null;
	isAnonymous: boolean;
	isPending: boolean;
	isAnalyzeDisabled: boolean;
	quotaRemaining: number;
	quotaLimit: number; // Week 2: Weekly quota limit from backend
	formattedResetTime: string; // When quota resets (e.g., "in 5 days (7:00 PM EST)")
	/** Hide Turnstile widget when quota modal is visible (prevents z-index overlap) */
	isQuotaModalVisible?: boolean;
	/** Mobile web keyboard visibility (show compact dropdowns when true) */
	isKeyboardVisible?: boolean;
	/** Current viewing mode/experience (for compact dropdown) */
	experienceMode?: TheatricalModeVariant;
	/** Explicit visibility state for the experience control seam. */
	experienceVisibility?: "hidden" | "compact" | "selector";
	/** Callback when experience mode changes */
	onExperienceModeChange?: (
		mode: TheatricalModeVariant,
		source?: "url_parameter" | "manual_toggle" | "adaptive_default",
	) => Promise<void>;
	/** Policy-selected placeholder text from the owning screen shell. */
	placeholderText?: string;
	onMessageChange: (text: string) => void;
	onInputHeightChange?: (height: number) => void;
	onContextChange: (context: ContextType) => void;
	onAnalyze: () => void;
	onPaste: (text: string) => void;
	onTurnstileSuccess: (token: string) => void;
	onTurnstileError: (error: string) => void;
	onTurnstileExpire: () => void;
	/** Ref for imperative TurnstileWidget reset (Phase 2) */
	turnstileRef?: React.Ref<TurnstileHandle>;
}

export function AnalysisSection({
	message,
	context,
	validationError,
	maxLength,
	turnstileError,
	clearanceToken,
	isAnonymous,
	isPending,
	isAnalyzeDisabled,
	quotaRemaining,
	quotaLimit,
	formattedResetTime,
	isQuotaModalVisible,
	isKeyboardVisible = false,
	experienceMode,
	experienceVisibility = "selector",
	onExperienceModeChange,
	placeholderText = "",
	onMessageChange,
	onInputHeightChange,
	onContextChange,
	onAnalyze,
	onPaste,
	onTurnstileSuccess,
	onTurnstileError,
	onTurnstileExpire,
	turnstileRef,
}: AnalysisSectionProps) {
	void React;
	const router = useRouter();
	const prevShowCompactControlsRef = React.useRef(false);
	const multimodalEnabled = useMultimodalFlag();

	// Derive quota visual state for styling
	function getQuotaState(): "exhausted" | "low" | "normal" {
		if (quotaRemaining === 0) return "exhausted";
		if (quotaRemaining <= 3) return "low";
		return "normal";
	}

	const quotaState = getQuotaState();

	const handleUpgrade = () => router.push("/subscription");
	const handleViewPlans = () => router.push("/subscription?tab=plans");

	const showCompactControls = isKeyboardVisible;

	// Scroll compact dropdowns into view when they mount (after keyboard appears)
	// This ensures the scroll happens AFTER React commits the render with dropdowns
	React.useLayoutEffect(() => {
		// Skip on non-web platforms (no document API)
		if (Platform.OS !== "web") return;

		// Detect transition from hidden → visible
		const wasHidden = !prevShowCompactControlsRef.current;
		const isNowVisible = showCompactControls;
		prevShowCompactControlsRef.current = showCompactControls;

		if (wasHidden && isNowVisible) {
			// Double RAF ensures dropdowns have final layout on slower devices
			// First RAF: React commits render
			// Second RAF: Browser paints dropdowns with correct dimensions
			let rafId2: number;
			const rafId1 = requestAnimationFrame(() => {
				rafId2 = requestAnimationFrame(() => {
					const section = document.querySelector(
						'[data-analysis-section="true"]',
					) as HTMLElement;
					if (section) {
						section.scrollIntoView({
							behavior: "smooth",
							block: "start",
							inline: "nearest",
						});
					}
				});
			});

			// Cleanup RAF on unmount or dependency change
			return () => {
				if (rafId1) cancelAnimationFrame(rafId1);
				if (rafId2) cancelAnimationFrame(rafId2);
			};
		}
	}, [showCompactControls]);

	return (
		<View
			{...(Platform.OS === "web" && { "data-analysis-section": "true" })}
			style={Platform.OS === "web" ? styles.webScrollMargin : undefined}
		>
			{/* Read-Only Selection Indicators - Shown when keyboard visible */}
			{showCompactControls && (
				<CompactReadOnlyIndicators
					experienceMode={experienceMode}
					context={context}
					showExperienceIndicator={experienceVisibility === "compact"}
				/>
			)}

			{/* Full Context Selector - Shown when keyboard is NOT visible */}
			{!showCompactControls && (
				<View className="mb-4">
					<ContextSelector value={context} onChange={onContextChange} />
				</View>
			)}

			{/* Message Input - Glass effect applied by MessageInput component */}
			{multimodalEnabled !== false && (
				<View className={isKeyboardVisible ? "" : "mb-4"}>
					<MessageInput
						value={message}
						onChangeText={onMessageChange}
						onHeightChange={onInputHeightChange}
						onPaste={onPaste}
						maxLength={maxLength}
						placeholder={placeholderText}
						isPending={isPending}
						isKeyboardVisible={isKeyboardVisible}
						isAnonymous={isAnonymous}
						onAnalyze={onAnalyze}
						isAnalyzeDisabled={
							isAnalyzeDisabled || (isAnonymous && quotaRemaining === 0)
						}
					/>
				</View>
			)}

			{/* Validation Error */}
			{validationError && (
				<View className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
					<TierBody size="sm" className="text-red-800">
						{validationError}
					</TierBody>
				</View>
			)}

			{/* Anonymous: Quota Counter (hidden during analysis/modal/keyboard) */}
			{isAnonymous &&
				!isPending &&
				!isQuotaModalVisible &&
				!showCompactControls && (
					<AnonymousQuotaCounter
						quotaRemaining={quotaRemaining}
						quotaLimit={quotaLimit}
						quotaState={quotaState}
						formattedResetTime={formattedResetTime}
						turnstileError={turnstileError}
						onUpgrade={handleUpgrade}
						onViewPlans={handleViewPlans}
					/>
				)}

			{/* Turnstile Widget — hidden when clearanceToken exists (DRAMA-WEB-28) */}
			{isAnonymous && !clearanceToken && (
				<View
					style={
						isPending || isQuotaModalVisible || showCompactControls
							? styles.hiddenTurnstileWidget
							: undefined
					}
					pointerEvents={
						isPending || isQuotaModalVisible || showCompactControls
							? "none"
							: "auto"
					}
				>
					<TurnstileWidget
						ref={turnstileRef}
						onSuccess={onTurnstileSuccess}
						onError={onTurnstileError}
						onExpire={onTurnstileExpire}
						theme="light"
					/>
				</View>
			)}
		</View>
	);
}

const styles = StyleSheet.create({
	webScrollMargin: { scrollMarginTop: 80 },
	hiddenTurnstileWidget: { height: 0, overflow: "hidden" },
});
